import React, { createContext, useContext, useState, useMemo, useEffect } from 'react'
import { Locale } from '../types/localization'
import { getI18nInstance, loadLocale, resources } from '../localization/translations'

interface I18nContextType {
  locale: Locale
  translate: (key: string) => string
  setLocale: (locale: Locale) => void
  isLoading: boolean
}

const I18nContext = createContext<I18nContextType | undefined>(undefined)

const isLocaleReady = (locale: Locale): boolean => Boolean(resources[(locale as string).toLowerCase() as Locale])

export const I18nProvider: React.FC<{ defaultLocale?: Locale; children: React.ReactNode }> = ({ defaultLocale = Locale.enIE, children }) => {
  const [locale, setLocale] = useState<Locale>(defaultLocale)
  // Bumped every time a locale's JSON chunk finishes loading so that
  // consumers re-render with the freshly available translations.
  const [loadVersion, setLoadVersion] = useState(0)
  // Start as "ready" if the locale was already preloaded (SSR, prior load,
  // or eager mount-time loadLocale). This is critical: lying about isLoading
  // on the very first render would cause a hydration mismatch.
  const [isLoading, setIsLoading] = useState(() => !isLocaleReady(defaultLocale))

  useEffect(() => {
    // Already loaded — nothing async to do, keep DOM stable.
    if (isLocaleReady(locale)) {
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    loadLocale(locale)
      .catch(() => {
        /* Failures are surfaced via translate() falling back to the key. */
      })
      .finally(() => {
        if (cancelled) return
        setLoadVersion((v) => v + 1)
        setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [locale])

  const contextValue = useMemo(() => {
    const i18n = getI18nInstance(locale)
    return {
      locale,
      translate: (key: string) => i18n.translate(key) ?? key,
      setLocale,
      isLoading,
    }
  }, [locale, loadVersion, isLoading])

  return <I18nContext.Provider value={contextValue}>{children}</I18nContext.Provider>
}

export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext)
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider')
  }
  return context
}
